"use client";

import { WatermarkedImage } from "@saas/shared/components/WatermarkedImage";
import { isImageGenerationTimeout } from "@shared/lib/image-generation-error";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import {
	EditIcon,
	RefreshCwIcon,
	SparklesIcon,
	StarIcon,
	Wand2Icon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { ImageActionLabels } from "./HistoryImageActions";
import { HistoryImageActions } from "./HistoryImageActions";
import { getImageEffect, getImageTool } from "./image-home";
import type { HistoryStatusLabels } from "./types";
import { DEFAULT_HISTORY_STATUS_LABELS, formatHistoryDate } from "./types";

/** Structural shape of one image generation — loose enough that both the feed's
 *  ImageHistoryItem and the image generator's own ImageGeneration fit. */
export type ImageHistoryCardItem = {
	id: string;
	originalPrompt: string;
	model: string;
	status?: string;
	errorMessage?: string | null;
	imageUrls: string[];
	inputImageUrls?: string[];
	favoriteImageIndexes?: number[];
	createdAt: string;
	metadata?: {
		/** 出图页面，10/23 起才有；工具页出的图卡头写工具名 */
		page?: string;
		parameters?: {
			num_outputs?: number;
			aspect_ratio?: string;
		};
	} | null;
};

/** "9:16" → CSS aspect-ratio。"auto" 之类拿不准的返回 undefined，交给图片自己撑高度 */
function parseAspectRatio(value?: string) {
	const match = value?.match(/^(\d+):(\d+)$/);
	return match ? `${match[1]} / ${match[2]}` : undefined;
}

interface ImageHistoryCardProps {
	item: ImageHistoryCardItem;
	statusLabels?: HistoryStatusLabels;
	/** 卡头那行的 "AI IMAGE" */
	typeLabel?: string;
	/** 把上游模型标识换成展示名；不传就直接显示 item.model */
	modelLabel?: (model: string) => string;
	/** 生成失败时那条红框里的话 */
	failedMessage?: string;
	/** 点图打开详情弹窗 */
	onOpenDetail?: (imageIndex: number) => void;
	openDetailLabel?: string;
	/** 回填表单。这一页没有图片表单时，由调用方跳到图片生成页去做 */
	onReprompt?: () => void;
	onRegenerate?: () => void;
	/** 这条记录自己正在重跑——按钮转圈 */
	isRegenerating?: boolean;
	/** 页面上有别的生成在跑，重跑先按住 */
	isRegenerateDisabled?: boolean;
	/** hover 工具条。省略就不渲染，卡片退回纯展示 */
	actionLabels?: ImageActionLabels;
	onGenerateVideo?: (imageUrl: string) => void;
	onDownload?: (imageUrl: string, imageIndex: number) => void;
	onEdit?: (imageUrl: string) => void;
	onDelete?: (imageIndex: number) => void;
	onToggleFavorite?: (imageIndex: number) => void;
	isImageFavorite?: (imageIndex: number) => boolean;
}

const ACTION_BUTTON_CLASS =
	"h-9 px-4 text-xs font-medium border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 rounded-lg gap-2 disabled:opacity-50 disabled:cursor-not-allowed";

/** The one image history card. Every page that shows a generation feed renders
 *  this — pages without an image form of their own (the video generator) point
 *  Reprompt/Regenerate at the image generator page instead of loading the
 *  generation back in. Props left out simply don't render. */
export function ImageHistoryCard({
	item,
	statusLabels = DEFAULT_HISTORY_STATUS_LABELS,
	typeLabel = "AI IMAGE",
	modelLabel,
	failedMessage = "Generation failed",
	onOpenDetail,
	openDetailLabel = "Open details",
	onReprompt,
	onRegenerate,
	isRegenerating,
	isRegenerateDisabled,
	actionLabels,
	onGenerateVideo,
	onDownload,
	onEdit,
	onDelete,
	onToggleFavorite,
	isImageFavorite,
}: ImageHistoryCardProps) {
	// PROCESSING 是异步任务收尾中（下载、转存），对用户来说还是「生成中」
	const isPending = item.status === "PENDING" || item.status === "PROCESSING";
	const isFailed = item.status === "FAILED";
	// 生成中还没有图，按用户要的出图数量占位，免得卡片高度跳一下
	const tileCount = isPending
		? item.metadata?.parameters?.num_outputs || 1
		: item.imageUrls?.length || 0;
	const isMultiple = tileCount > 1;
	// 出图前先按当初选的比例占位，图到了高度就不跳
	const pendingAspectRatio = parseAspectRatio(
		item.metadata?.parameters?.aspect_ratio,
	);
	const t = useTranslations();
	const tool = getImageTool(item);
	const effect = getImageEffect(item);

	return (
		<div className="transition-all bg-white dark:bg-card border border-zinc-200/20 dark:border-border/10 rounded-xl p-3 sm:p-6 hover:shadow-sm">
			{/* Metadata Line */}
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4">
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] sm:text-xs text-zinc-500">
					<div className="flex items-center space-x-1.5 font-bold text-zinc-700 dark:text-zinc-300">
						{tool ? (
							<Wand2Icon className="h-3.5 w-3.5" />
						) : effect ? (
							<StarIcon className="h-3.5 w-3.5" />
						) : (
							<SparklesIcon className="h-3.5 w-3.5" />
						)}
						<span>
							{tool
								? `TOOLS · ${t(tool.titleKey)}`
								: effect
									? `EFFECT · ${t(effect.titleKey)}`
									: typeLabel}
						</span>
					</div>
					<span className="hidden sm:inline text-zinc-300">|</span>
					<span>{formatHistoryDate(item.createdAt)}</span>
					<span className="hidden sm:inline text-zinc-300">|</span>
					<span className="font-medium">
						{modelLabel?.(item.model) ?? item.model}
					</span>
				</div>
				{/* 成功不出徽章——图就在下面摆着，再标一句"成功"是废话 */}
				{(isFailed || isPending) && (
					<div
						className={cn(
							"self-start sm:self-auto text-xs sm:text-[11px] font-semibold px-2.5 py-0.5 rounded-md",
							isFailed
								? "bg-red-50 text-red-600"
								: "bg-blue-50 text-blue-600",
						)}
					>
						{isFailed
							? statusLabels.failed
							: statusLabels.generating}
					</div>
				)}
			</div>

			{/* Prompt with the input images */}
			<div className="mb-4 sm:mb-5 flex flex-col sm:flex-row gap-3 sm:gap-4 items-start">
				{item.inputImageUrls && item.inputImageUrls.length > 0 && (
					<div className="flex-shrink-0 flex gap-2">
						{item.inputImageUrls.map((inputImageUrl) => (
							<div
								key={inputImageUrl}
								className="relative rounded-lg overflow-hidden bg-zinc-50 w-14 h-14 sm:w-[72px] sm:h-[72px] flex-shrink-0"
							>
								<img
									src={inputImageUrl}
									alt="Input"
									className="w-full h-full object-cover"
								/>
							</div>
						))}
					</div>
				)}
				<div className="flex-1 min-w-0">
					<p className="text-[15px] text-zinc-800 dark:text-zinc-200 leading-relaxed line-clamp-3">
						{item.originalPrompt}
					</p>
				</div>
			</div>

			{isFailed && (
				<div className="mb-4 p-4 bg-red-50/50 border border-red-100 rounded-lg text-sm text-red-600 flex items-start">
					<span className="mr-2">⚠️</span>
					{isImageGenerationTimeout(item.errorMessage)
						? t("aiImageGenerator.client.imageSettings.errors.generationTimeout")
						: failedMessage}
				</div>
			)}

			{tileCount > 0 && !isFailed && (
				<div className="mb-4 sm:mb-5">
					<div
						className={cn(
							isMultiple
								? "grid grid-cols-2 md:grid-cols-4 gap-3 items-start"
								: "flex gap-3 sm:gap-4",
						)}
					>
						{isPending
							? Array.from({ length: tileCount }).map(
									(_, index) => (
										<div
											// biome-ignore lint/suspicious/noArrayIndexKey: placeholders have nothing else to key on
											key={index}
											className={cn(
												"relative rounded-lg overflow-hidden bg-base-200",
												!pendingAspectRatio &&
													"aspect-square",
												isMultiple
													? "w-full"
													: "w-full sm:max-w-[280px]",
											)}
											style={
												pendingAspectRatio
													? {
															aspectRatio:
																pendingAspectRatio,
														}
													: undefined
											}
										>
											<div className="skeleton h-full w-full" />
											<div className="absolute inset-0 flex items-center justify-center">
												<span className="loading loading-infinity loading-lg text-blue-500" />
											</div>
										</div>
									),
								)
							: item.imageUrls.map((imageUrl, imageIndex) => (
									<div
										key={imageUrl}
										className={cn(
											"relative group rounded-lg overflow-hidden bg-zinc-50",
											isMultiple
												? "w-full"
												: "w-full sm:max-w-[280px]",
										)}
									>
										{/* 不裁比例：竖图就是竖的，宽图就是宽的 */}
										<WatermarkedImage
											src={imageUrl}
											alt={item.originalPrompt}
											loading="lazy"
											draggable={false}
											className="select-none rounded-lg w-full h-auto"
										/>
										{/* 点图开详情；放在工具条之前，DOM 靠后的它才压得住 */}
										{onOpenDetail && (
											<button
												type="button"
												aria-label={openDetailLabel}
												className="absolute inset-0 cursor-pointer"
												onClick={() =>
													onOpenDetail(imageIndex)
												}
											/>
										)}
										{actionLabels && onDownload && (
											<HistoryImageActions
												labels={actionLabels}
												onGenerateVideo={
													onGenerateVideo
														? () =>
																onGenerateVideo(
																	imageUrl,
																)
														: undefined
												}
												onDownload={() =>
													onDownload(
														imageUrl,
														imageIndex,
													)
												}
												onEdit={
													onEdit
														? () => onEdit(imageUrl)
														: undefined
												}
												onDelete={
													onDelete
														? () =>
																onDelete(
																	imageIndex,
																)
														: undefined
												}
												onFavorite={
													onToggleFavorite
														? () =>
																onToggleFavorite(
																	imageIndex,
																)
														: undefined
												}
												isFavorite={isImageFavorite?.(
													imageIndex,
												)}
											/>
										)}
									</div>
								))}
					</div>
				</div>
			)}

			{(onReprompt || onRegenerate) && (
				<div className="flex flex-wrap items-center justify-start gap-2 pt-2">
					{onReprompt && (
						<Button
							variant="outline"
							size="sm"
							onClick={onReprompt}
							disabled={isPending}
							className={ACTION_BUTTON_CLASS}
						>
							<EditIcon className="h-3.5 w-3.5" />
							Reprompt
						</Button>
					)}
					{/* 失败的记录没什么可重跑的，参数照旧只会再失败一次 */}
					{onRegenerate && !isFailed && (
						<Button
							variant="outline"
							size="sm"
							onClick={onRegenerate}
							disabled={isPending || isRegenerateDisabled}
							className={ACTION_BUTTON_CLASS}
						>
							<RefreshCwIcon
								className={cn(
									"h-3.5 w-3.5",
									(isPending || isRegenerating) &&
										"animate-spin",
								)}
							/>
							Regenerate
						</Button>
					)}
				</div>
			)}
		</div>
	);
}
